import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiArrowLeft,
  FiDownload,
  FiEdit,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { ConfirmDialog } from "./common/ConfirmDialog";
import { DeleteFieldModal, ExtraColumnHeader, RenameFieldModal } from "./catalog/CatalogFieldModals";
import { laborScopeCodesService } from "../services/api/laborScopeCodesService";
import { loadXLSX } from "../utils/xlsxLoader";
import "./StockInventoryItemsManage.css";
import "./LaborScopeCodesManage.css";

const CORE_COLUMNS = [
  { key: "scope_code", label: "Scope Code" },
  { key: "scope_name", label: "Scope Name" },
  { key: "description_code", label: "Description Code" },
  { key: "description_name", label: "Description Name" },
  { key: "is_active", label: "Is Active" },
  { key: "notes", label: "Notes" },
];

const EMPTY_FORM = {
  scope_code: "",
  scope_name: "",
  description_code: "",
  description_name: "",
  is_active: true,
  notes: "",
};

function normalizeExtraFields(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const extra = {};
  Object.entries(value).forEach(([key, val]) => {
    const name = String(key || "").trim();
    if (!name) return;
    extra[name] = val == null ? "" : String(val);
  });
  return extra;
}

function extraFieldsToRows(extraFields, extraKeys) {
  const extra = normalizeExtraFields(extraFields);
  const keys = [...extraKeys];
  Object.keys(extra).forEach((key) => {
    if (!keys.includes(key)) keys.push(key);
  });
  if (keys.length === 0) return [{ key: "", value: "" }];
  return keys.map((key) => ({ key, value: extra[key] || "" }));
}

function FieldLabel({ children, required = false }) {
  return (
    <span className="stock-inv-field-name">
      {children}
      {required ? <span className="stock-inv-required">*</span> : null}
    </span>
  );
}

function withCurrentOption(options, current) {
  const value = String(current || "").trim();
  if (!value) return options;
  if (options.some((opt) => opt === value)) return options;
  return [value, ...options];
}

function rowsToExtraFields(rows) {
  const extra = {};
  (rows || []).forEach((row) => {
    const key = String(row.key || "").trim();
    if (!key) return;
    extra[key] = String(row.value || "").trim();
  });
  return extra;
}

function useModalLock(isOpen, onClose) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);
}

function ItemFormModal({
  isOpen,
  mode,
  initialItem,
  extraKeys,
  scopeNameOptions,
  columnLabels = {},
  hiddenOptionalKeys = [],
  onClose,
  onSave,
  isSaving,
  error,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [extraRows, setExtraRows] = useState([{ key: "", value: "" }]);
  const [isCustomScopeName, setIsCustomScopeName] = useState(false);

  useModalLock(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    if (initialItem) {
      setForm({
        scope_code: initialItem.scope_code || "",
        scope_name: initialItem.scope_name || "",
        description_code: initialItem.description_code || "",
        description_name: initialItem.description_name || "",
        is_active: initialItem.is_active !== false,
        notes: initialItem.notes || "",
      });
      setExtraRows(extraFieldsToRows(initialItem.extra_fields, extraKeys));
      setIsCustomScopeName(
        Boolean(initialItem.scope_name) &&
          !scopeNameOptions.some((value) => value === initialItem.scope_name)
      );
    } else {
      setForm(EMPTY_FORM);
      setExtraRows(extraFieldsToRows({}, extraKeys));
      setIsCustomScopeName(scopeNameOptions.length === 0);
    }
  }, [isOpen, initialItem, extraKeys, scopeNameOptions]);

  if (!isOpen) return null;

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateExtraRow = (index, patch) => {
    setExtraRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addExtraRow = () => {
    setExtraRows((prev) => [...prev, { key: "", value: "" }]);
  };

  const removeExtraRow = (index) => {
    setExtraRows((prev) => (prev.length <= 1 ? [{ key: "", value: "" }] : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave({
      ...form,
      extra_fields: rowsToExtraFields(extraRows),
    });
  };

  return createPortal(
    <div className="stock-inv-modal-overlay labor-scope-theme" onClick={onClose} role="presentation">
      <div
        className="stock-inv-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="labor-scope-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="stock-inv-modal-header">
          <div>
            <h3 id="labor-scope-modal-title">
              {mode === "edit" ? "Edit Labor & Scope Code" : "Add Labor & Scope Code"}
            </h3>
            <p>Match the Excel master sheet fields. Extra fields stay on this row.</p>
          </div>
          <button type="button" className="stock-inv-icon-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <form className="stock-inv-form" onSubmit={handleSubmit}>
          <div className="stock-inv-modal-body">
            <div className="stock-inv-form-grid">
              <label>
                <FieldLabel required>{columnLabels.scope_code || "Scope Code"}</FieldLabel>
                <input
                  value={form.scope_code}
                  onChange={(e) => updateField("scope_code", e.target.value)}
                  required
                />
              </label>
              <label>
                <FieldLabel required>{columnLabels.scope_name || "Scope Name"}</FieldLabel>
                <select
                  value={isCustomScopeName ? "__custom__" : form.scope_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "__custom__") {
                      setIsCustomScopeName(true);
                      updateField("scope_name", "");
                      return;
                    }
                    setIsCustomScopeName(false);
                    updateField("scope_name", value);
                  }}
                  required
                >
                  <option value="" disabled>
                    Select scope name
                  </option>
                  {withCurrentOption(scopeNameOptions, isCustomScopeName ? "" : form.scope_name).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                  <option value="__custom__">Add new scope name…</option>
                </select>
              </label>
              {isCustomScopeName ? (
                <label>
                  <FieldLabel required>New scope name</FieldLabel>
                  <input
                    value={form.scope_name}
                    onChange={(e) => updateField("scope_name", e.target.value)}
                    placeholder="e.g. Roof Deck"
                    required
                  />
                </label>
              ) : null}
              <label>
                <FieldLabel required>{columnLabels.description_code || "Description Code"}</FieldLabel>
                <input
                  value={form.description_code}
                  onChange={(e) => updateField("description_code", e.target.value)}
                  required
                />
              </label>
              <label>
                <FieldLabel required>{columnLabels.description_name || "Description Name"}</FieldLabel>
                <input
                  value={form.description_name}
                  onChange={(e) => updateField("description_name", e.target.value)}
                  required
                />
              </label>
              {hiddenOptionalKeys.includes("is_active") ? null : (
              <label>
                <FieldLabel required>{columnLabels.is_active || "Is Active"}</FieldLabel>
                <select
                  value={form.is_active ? "true" : "false"}
                  onChange={(e) => updateField("is_active", e.target.value === "true")}
                  required
                >
                  <option value="true">TRUE</option>
                  <option value="false">FALSE</option>
                </select>
              </label>
              )}
              {hiddenOptionalKeys.includes("notes") ? null : (
              <label>
                <FieldLabel>{columnLabels.notes || "Notes"}</FieldLabel>
                <input
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                />
              </label>
              )}
            </div>

            <div className="stock-inv-extra-block">
              <div className="stock-inv-extra-header">
                <div>
                  <h4>Additional fields</h4>
                  <p>Add any extra column that is not in the master Excel sheet.</p>
                </div>
                <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={addExtraRow}>
                  <FiPlus /> Add field
                </button>
              </div>
              <div className="stock-inv-extra-list">
                {extraRows.map((row, index) => (
                  <div key={`${row.key}-${index}`} className="stock-inv-extra-row">
                    <input
                      placeholder="Field name"
                      value={row.key}
                      onChange={(e) => updateExtraRow(index, { key: e.target.value })}
                    />
                    <input
                      placeholder="Value"
                      value={row.value}
                      onChange={(e) => updateExtraRow(index, { value: e.target.value })}
                    />
                    <button
                      type="button"
                      className="stock-inv-icon-btn stock-inv-icon-btn-danger"
                      onClick={() => removeExtraRow(index)}
                      aria-label="Remove field"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {error ? <div className="stock-inv-form-error">{error}</div> : null}
          </div>

          <div className="stock-inv-modal-actions">
            <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button type="submit" className="stock-inv-btn stock-inv-btn-primary" disabled={isSaving}>
              {isSaving ? "Saving..." : mode === "edit" ? "Save changes" : "Create item"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function AddFieldModal({ isOpen, onClose, onAdd, existingKeys }) {
  const [fieldName, setFieldName] = useState("");
  const [error, setError] = useState("");

  useModalLock(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      setFieldName("");
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const name = fieldName.trim();
    if (!name) {
      setError("Enter a field name.");
      return;
    }
    const taken = existingKeys.some((key) => key.toLowerCase() === name.toLowerCase());
    if (taken) {
      setError("That field already exists.");
      return;
    }
    onAdd(name);
  };

  return createPortal(
    <div className="stock-inv-modal-overlay labor-scope-theme" onClick={onClose} role="presentation">
      <div className="stock-inv-modal stock-inv-modal-sm" onClick={(event) => event.stopPropagation()}>
        <div className="stock-inv-modal-header">
          <div>
            <h3>Add field</h3>
            <p>This column will appear on every row. Fill values when you edit a row.</p>
          </div>
          <button type="button" className="stock-inv-icon-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <form className="stock-inv-form" onSubmit={handleSubmit}>
          <div className="stock-inv-modal-body">
            <label>
              <FieldLabel>Field name</FieldLabel>
              <input
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                placeholder="e.g. Notes"
                autoFocus
              />
            </label>
            {error ? <div className="stock-inv-form-error">{error}</div> : null}
          </div>
          <div className="stock-inv-modal-actions">
            <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="stock-inv-btn stock-inv-btn-primary">
              Add field
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function LaborScopeCodesManage({ onBack, onToast }) {
  const [items, setItems] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [addedFieldKeys, setAddedFieldKeys] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingItem, setEditingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [isAddFieldOpen, setIsAddFieldOpen] = useState(false);
  const [isRenameFieldOpen, setIsRenameFieldOpen] = useState(false);
  const [renameFieldFrom, setRenameFieldFrom] = useState("");
  const [isRenamingField, setIsRenamingField] = useState(false);
  const [renameFieldError, setRenameFieldError] = useState("");
  const [isDeleteFieldOpen, setIsDeleteFieldOpen] = useState(false);
  const [deleteFieldKey, setDeleteFieldKey] = useState("");
  const [isDeletingField, setIsDeletingField] = useState(false);
  const [deleteFieldError, setDeleteFieldError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const [response, columnsResponse] = await Promise.all([
        laborScopeCodesService.listCatalog(),
        laborScopeCodesService.listColumns(),
      ]);
      setItems(Array.isArray(response?.data) ? response.data : []);
      setColumns(Array.isArray(columnsResponse?.data) ? columnsResponse.data : []);
    } catch (error) {
      console.error("Failed to load labor/scope codes:", error);
      setLoadError(error?.response?.data?.message || "Failed to load labor & scope codes.");
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const visibleColumns = useMemo(() => {
    const visible = columns.filter((col) => col.visible);
    if (visible.length > 0) return visible;
    return CORE_COLUMNS.map((col) => ({ ...col, visible: true, extra: false, required: true }));
  }, [columns]);
  const extraKeys = useMemo(() => {
    const keys = visibleColumns.filter((col) => col.extra).map((col) => col.key);
    const seen = new Set(keys.map((key) => key.toLowerCase()));
    items.forEach((item) => {
      Object.keys(normalizeExtraFields(item.extra_fields)).forEach((key) => {
        const lower = key.toLowerCase();
        if (seen.has(lower)) return;
        seen.add(lower);
        keys.push(key);
      });
    });
    addedFieldKeys.forEach((key) => {
      const lower = key.toLowerCase();
      if (seen.has(lower)) return;
      seen.add(lower);
      keys.push(key);
    });
    return keys;
  }, [items, addedFieldKeys, visibleColumns]);
  const columnLabels = useMemo(() => {
    const labels = {};
    columns.forEach((col) => {
      labels[col.key] = col.label;
    });
    return labels;
  }, [columns]);
  const hiddenOptionalKeys = useMemo(
    () => columns.filter((col) => !col.visible && !col.required && !col.extra).map((col) => col.key),
    [columns]
  );

  const scopeNameOptions = useMemo(() => {
    return Array.from(new Set(items.map((item) => item.scope_name).filter(Boolean))).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return items.filter((item) => {
      if (scopeFilter && item.scope_name !== scopeFilter) return false;
      if (!term) return true;
      const extraText = Object.entries(normalizeExtraFields(item.extra_fields))
        .map(([key, value]) => `${key} ${value}`)
        .join(" ");
      const haystack = [
        item.scope_code,
        item.scope_name,
        item.description_code,
        item.description_name,
        item.notes,
        item.is_active === false ? "false inactive" : "true active",
        extraText,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [items, searchTerm, scopeFilter]);

  const openCreate = () => {
    setFormMode("create");
    setEditingItem(null);
    setFormError("");
    setIsFormOpen(true);
  };

  const openEdit = (item) => {
    setFormMode("edit");
    setEditingItem(item);
    setFormError("");
    setIsFormOpen(true);
  };

  const handleSave = async (payload) => {
    setIsSaving(true);
    setFormError("");
    try {
      if (formMode === "edit" && editingItem?.id) {
        await laborScopeCodesService.updateItem(editingItem.id, payload);
        onToast?.("Labor/scope code updated.");
      } else {
        await laborScopeCodesService.createItem(payload);
        onToast?.("Labor/scope code created.");
      }
      setIsFormOpen(false);
      setEditingItem(null);
      await loadItems();
    } catch (error) {
      setFormError(error?.response?.data?.message || "Failed to save labor/scope code.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await laborScopeCodesService.deleteItem(deleteTarget.id);
      onToast?.(`Deleted ${deleteTarget.description_code}.`);
      setDeleteTarget(null);
      await loadItems();
    } catch (error) {
      onToast?.(error?.response?.data?.message || "Failed to delete labor/scope code.");
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportToExcel = async () => {
    if (isExporting || isLoading || filteredItems.length === 0) return;

    try {
      setIsExporting(true);
      const XLSX = await loadXLSX();
      if (!XLSX) {
        onToast?.("Unable to download Excel right now. Please try again.");
        return;
      }

      const headers = visibleColumns.map((col) => col.label);
      const rows = filteredItems.map((item) => {
        const extra = normalizeExtraFields(item.extra_fields);
        return visibleColumns.map((col) => {
          if (col.extra) return extra[col.key] || "";
          if (col.key === "is_active") return item.is_active === false ? "FALSE" : "TRUE";
          return item[col.key] || "";
        });
      });

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "LaborAndScopeCodes");
      XLSX.writeFile(workbook, "LaborAndScopeCodes.xlsx");
      onToast?.(`Downloaded ${filteredItems.length} labor/scope code${filteredItems.length === 1 ? "" : "s"}.`);
    } catch (error) {
      console.error("Failed to export labor/scope codes:", error);
      onToast?.("Failed to download Excel file.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddField = async (name) => {
    try {
      await laborScopeCodesService.addColumn(name);
      setAddedFieldKeys((prev) => (prev.includes(name) ? prev : [...prev, name]));
      setIsAddFieldOpen(false);
      onToast?.(`Added field "${name}". Edit a row to fill its value.`);
      await loadItems();
    } catch (error) {
      onToast?.(error?.response?.data?.message || "Failed to add field.");
    }
  };

  const openRenameField = (key = "") => {
    if (visibleColumns.length === 0) return;
    setRenameFieldFrom(key);
    setRenameFieldError("");
    setIsRenameFieldOpen(true);
  };

  const openDeleteField = (key = "") => {
    if (visibleColumns.length === 0) return;
    setDeleteFieldKey(key);
    setDeleteFieldError("");
    setIsDeleteFieldOpen(true);
  };

  const handleRenameField = async (from, to) => {
    const current = visibleColumns.find((col) => col.key === from || col.label === from);
    if (current?.label === to) {
      setIsRenameFieldOpen(false);
      return;
    }
    setIsRenamingField(true);
    setRenameFieldError("");
    try {
      await laborScopeCodesService.renameExtraField(from, to);
      setAddedFieldKeys((prev) => {
        const next = prev.map((key) => (key.toLowerCase() === from.toLowerCase() ? to : key));
        if (!next.some((key) => key.toLowerCase() === to.toLowerCase())) next.push(to);
        return next.filter((key, index, list) => list.findIndex((item) => item.toLowerCase() === key.toLowerCase()) === index);
      });
      setIsRenameFieldOpen(false);
      onToast?.(`Renamed field to "${to}".`);
      await loadItems();
    } catch (error) {
      setRenameFieldError(error?.response?.data?.message || "Failed to rename field.");
    } finally {
      setIsRenamingField(false);
    }
  };

  const handleDeleteExtraField = async (key) => {
    setIsDeletingField(true);
    setDeleteFieldError("");
    try {
      await laborScopeCodesService.deleteExtraField(key);
      setAddedFieldKeys((prev) => prev.filter((name) => name.toLowerCase() !== key.toLowerCase()));
      setIsDeleteFieldOpen(false);
      onToast?.(`Deleted field.`);
      await loadItems();
    } catch (error) {
      setDeleteFieldError(error?.response?.data?.message || "Failed to delete field.");
    } finally {
      setIsDeletingField(false);
    }
  };

  const existingFieldKeys = [
    ...visibleColumns.map((col) => col.label),
    ...visibleColumns.map((col) => col.key),
  ];

  return (
    <div className="config-sub-view fade-in stock-inv-manage labor-scope-theme">
      <div className="sub-view-nav" onClick={onBack}>
        <FiArrowLeft /> Back to Upload Labor &amp; Scope Codes
      </div>

      <header className="sub-view-header stock-inv-header">
        <div className="header-icon-badge">
          <FiUploadCloud />
        </div>
        <div className="stock-inv-header-copy">
          <h2>Manage Labor &amp; Scope Codes</h2>
          <p>
            Edit the catalog stored in the database. Create, update, or delete rows, and add, rename, or delete any
            column when needed. A later Excel upload must use these column names, in this order.
          </p>
        </div>
        <div className="stock-inv-header-actions">
          <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={() => setIsAddFieldOpen(true)}>
            <FiPlus /> Add field
          </button>
          <button type="button" className="stock-inv-btn stock-inv-btn-primary" onClick={openCreate}>
            <FiPlus /> Add item
          </button>
        </div>
      </header>

      <div className="sub-view-card stock-inv-card">
        <div className="stock-inv-toolbar">
          <div className="stock-inv-search">
            <FiSearch />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search scope code, description..."
            />
          </div>
          <select
            className="stock-inv-filter"
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
          >
            <option value="">{`All ${(columnLabels.scope_name || "scope name").toLowerCase()}s`}</option>
            {scopeNameOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <div className="stock-inv-count">
            {filteredItems.length} of {items.length} item{items.length === 1 ? "" : "s"}
          </div>
          <button
            type="button"
            className="stock-inv-export-btn"
            onClick={handleExportToExcel}
            disabled={isLoading || isExporting || filteredItems.length === 0}
            title="Download Excel"
            aria-label="Download Excel"
          >
            <FiDownload />
          </button>
        </div>

        <div className="stock-inv-table-wrap">
          <table className="users-table stock-inv-table">
            <thead>
              <tr>
                {visibleColumns.map((col) => (
                  <th key={col.key}>
                    <ExtraColumnHeader
                      columnKey={col.key}
                      name={col.label}
                      onRename={openRenameField}
                      onDelete={openDeleteField}
                    />
                  </th>
                ))}
                <th className="stock-inv-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="users-table-empty">
                    Loading labor &amp; scope codes...
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="users-table-empty">
                    {loadError}
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="users-table-empty">
                    No labor &amp; scope codes found. Add a row or upload the Excel master sheet.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const extra = normalizeExtraFields(item.extra_fields);
                  return (
                    <tr key={item.id}>
                      {visibleColumns.map((col) => {
                        if (col.extra) {
                          return <td key={col.key}>{extra[col.key] || "—"}</td>;
                        }
                        if (col.key === "scope_code") {
                          return (
                            <td key={col.key} className="stock-inv-id-cell">
                              {item.scope_code}
                            </td>
                          );
                        }
                        if (col.key === "scope_name") {
                          return (
                            <td key={col.key}>
                              <span className="stock-inv-chip">{item.scope_name}</span>
                            </td>
                          );
                        }
                        if (col.key === "description_name") {
                          return (
                            <td key={col.key} className="stock-inv-desc-cell">
                              {item.description_name}
                            </td>
                          );
                        }
                        if (col.key === "is_active") {
                          return (
                            <td key={col.key}>
                              {item.is_active === false ? (
                                <span className="labor-scope-inactive">FALSE</span>
                              ) : (
                                <span className="stock-inv-chip">TRUE</span>
                              )}
                            </td>
                          );
                        }
                        return <td key={col.key}>{item[col.key] || "—"}</td>;
                      })}
                      <td className="stock-inv-actions-col">
                        <button
                          type="button"
                          className="stock-inv-icon-btn"
                          title="Edit"
                          onClick={() => openEdit(item)}
                        >
                          <FiEdit />
                        </button>
                        <button
                          type="button"
                          className="stock-inv-icon-btn stock-inv-icon-btn-danger"
                          title="Delete"
                          onClick={() => setDeleteTarget(item)}
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ItemFormModal
        isOpen={isFormOpen}
        mode={formMode}
        initialItem={editingItem}
        extraKeys={extraKeys}
        scopeNameOptions={scopeNameOptions}
        columnLabels={columnLabels}
        hiddenOptionalKeys={hiddenOptionalKeys}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSave}
        isSaving={isSaving}
        error={formError}
      />

      <AddFieldModal
        isOpen={isAddFieldOpen}
        onClose={() => setIsAddFieldOpen(false)}
        onAdd={handleAddField}
        existingKeys={existingFieldKeys}
      />

      <RenameFieldModal
        isOpen={isRenameFieldOpen}
        fields={visibleColumns}
        extraKeys={extraKeys}
        initialFrom={renameFieldFrom}
        existingKeys={existingFieldKeys}
        overlayClassName="labor-scope-theme"
        onClose={() => {
          if (!isRenamingField) setIsRenameFieldOpen(false);
        }}
        onRename={handleRenameField}
        isSaving={isRenamingField}
        error={renameFieldError}
      />

      <DeleteFieldModal
        isOpen={isDeleteFieldOpen}
        fields={visibleColumns}
        extraKeys={extraKeys}
        initialKey={deleteFieldKey}
        overlayClassName="labor-scope-theme"
        onClose={() => {
          if (!isDeletingField) setIsDeleteFieldOpen(false);
        }}
        onDelete={handleDeleteExtraField}
        isSaving={isDeletingField}
        error={deleteFieldError}
      />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Delete labor/scope code?"
        message={
          deleteTarget
            ? `Delete ${deleteTarget.description_code} — ${deleteTarget.description_name}? This removes it from the master catalog.`
            : ""
        }
        confirmLabel="Delete"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
      />
    </div>
  );
}

export default LaborScopeCodesManage;
